import { jsPDF } from 'jspdf'
import { Form1008Data } from '../types'
import { calculateMetrics } from './calculations'

const PAGE_WIDTH = 216
const PAGE_HEIGHT = 279
const LEFT = 6
const RIGHT = 210
const WIDTH = RIGHT - LEFT
const FOOTER_Y = 271
const BLACK: [number, number, number] = [0, 0, 0]
const GRAY: [number, number, number] = [94, 94, 94]

type Pdf = jsPDF

function setFont(doc: Pdf, size = 6.2, bold = false) {
  doc.setFont('helvetica', bold ? 'bold' : 'normal')
  doc.setFontSize(size)
  doc.setTextColor(...BLACK)
}

function money(value: number, decimals = 0) {
  return `$${Number(value || 0).toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })}`
}

function percent(value: number, decimals = 2) {
  return Number.isFinite(value) ? `${value.toFixed(decimals)}%` : '—'
}

function textOrDash(value: string | number | null | undefined) {
  return value === '' || value === null || value === undefined
    ? '—'
    : String(value)
}

function enumLabel(
  value: string,
  choices: Record<string, string>,
  fallback = '—',
) {
  return choices[value] || fallback
}

function safeFileName(value: string) {
  return value.replace(/[^a-z0-9_-]+/gi, '-').replace(/^-+|-+$/g, '')
}

function shortText(doc: Pdf, value: string, maxWidth: number, size = 6.2) {
  setFont(doc, size)
  if (doc.getTextWidth(value) <= maxWidth) return value

  const suffix = '…'
  let result = value
  while (
    result.length > 1 &&
    doc.getTextWidth(`${result}${suffix}`) > maxWidth
  ) {
    result = result.slice(0, -1)
  }
  return `${result}${suffix}`
}

function rule(doc: Pdf, x: number, y: number, width: number) {
  doc.setDrawColor(...GRAY)
  doc.setLineWidth(0.22)
  doc.line(x, y, x + width, y)
}

function sectionBar(doc: Pdf, y: number, title: string) {
  doc.setFillColor(...BLACK)
  doc.rect(LEFT, y, WIDTH, 3.5, 'F')
  setFont(doc, 6.35, true)
  doc.setTextColor(255, 255, 255)
  doc.text(title, LEFT + 1.1, y + 2.42)
  doc.setTextColor(...BLACK)
}

function field(
  doc: Pdf,
  x: number,
  y: number,
  width: number,
  label: string,
  value: string,
  options?: { valueX?: number; boldValue?: boolean; lineOffset?: number },
) {
  setFont(doc, 5.75, true)
  doc.text(label, x, y)

  const valueX = options?.valueX ?? x
  const lineOffset = options?.lineOffset ?? 3.15
  setFont(doc, 6.45, options?.boldValue ?? false)
  doc.text(shortText(doc, value, x + width - valueX, 6.45), valueX, y + 2.05)
  rule(doc, x, y + lineOffset, width)
}

function inlineField(
  doc: Pdf,
  x: number,
  y: number,
  width: number,
  label: string,
  value: string,
  labelWidth: number,
) {
  setFont(doc, 5.85, true)
  doc.text(label, x, y)
  setFont(doc, 6.35)
  doc.text(shortText(doc, value, width - labelWidth, 6.35), x + labelWidth, y)
  rule(doc, x, y + 1.3, width)
}

function checkbox(
  doc: Pdf,
  x: number,
  y: number,
  label: string,
  selected: boolean,
) {
  doc.setDrawColor(...BLACK)
  doc.setLineWidth(0.22)
  doc.rect(x, y - 1.35, 1.35, 1.35)

  if (selected) {
    doc.setLineWidth(0.38)
    doc.line(x + 0.18, y - 0.72, x + 0.55, y - 0.28)
    doc.line(x + 0.55, y - 0.28, x + 1.2, y - 1.12)
  }

  setFont(doc, 6)
  doc.text(label, x + 1.95, y - 0.2)
}

function checkboxList(
  doc: Pdf,
  x: number,
  y: number,
  title: string,
  rows: { label: string; selected: boolean }[],
  gap = 2.8,
) {
  setFont(doc, 6, true)
  doc.text(title, x, y)
  rows.forEach((row, index) => {
    checkbox(doc, x, y + 3.05 + index * gap, row.label, row.selected)
  })
}

function amountLine(
  doc: Pdf,
  x: number,
  y: number,
  label: string,
  value: string,
  width: number,
  valueWidth = 18,
) {
  setFont(doc, 5.95)
  doc.text(label, x, y)
  setFont(doc, 6.25)
  doc.text(shortText(doc, value, valueWidth, 6.25), x + width, y, {
    align: 'right',
  })
  rule(doc, x + width - valueWidth, y + 1.25, valueWidth)
}

function footer(doc: Pdf) {
  const pages = doc.getNumberOfPages()
  for (let page = 1; page <= pages; page += 1) {
    doc.setPage(page)
    rule(doc, LEFT, FOOTER_Y - 2.6, WIDTH)
    setFont(doc, 5.7)
    doc.text('Freddie Mac Form 1077     12/18', LEFT + 12, FOOTER_Y + 1)
    doc.text(`Page ${page} of ${pages}`, PAGE_WIDTH / 2, FOOTER_Y + 1, {
      align: 'center',
    })
    doc.text('Fannie Mae Form 1008     12/18', RIGHT - 12, FOOTER_Y + 1, {
      align: 'right',
    })
  }
}

function header(doc: Pdf, continuation = false) {
  setFont(doc, 9.2, true)
  doc.text(
    continuation
      ? 'Uniform Underwriting and Transmittal Summary — Continuation'
      : 'Uniform Underwriting and Transmittal Summary',
    PAGE_WIDTH / 2,
    5.8,
    { align: 'center' },
  )
}

function drawSectionFour(doc: Pdf, y: number, data: Form1008Data) {
  sectionBar(doc, y, 'IV. Seller and Contact Information')
  const row1 = y + 6
  const row2 = row1 + 3.65
  const row3 = row2 + 3.65
  const row4 = row3 + 3.65

  inlineField(doc, 8, row1, 63, 'Seller Name', textOrDash(data.sellerName), 13)
  inlineField(
    doc,
    77,
    row1,
    51,
    'Contact Name',
    textOrDash(data.contactName),
    17,
  )
  inlineField(
    doc,
    136,
    row1,
    57,
    'Contact Phone Number',
    textOrDash(data.contactPhone),
    27,
  )

  inlineField(
    doc,
    8,
    row2,
    63,
    'Seller Address',
    textOrDash(data.sellerAddress),
    16,
  )
  inlineField(
    doc,
    77,
    row2,
    51,
    'Contact Title',
    textOrDash(data.contactTitle),
    15,
  )
  inlineField(
    doc,
    136,
    row2,
    57,
    'Alternate Phone',
    textOrDash(data.contactPhoneAlt),
    22,
  )

  inlineField(doc, 8, row3, 63, 'Seller No.', textOrDash(data.sellerNumber), 12)
  inlineField(
    doc,
    77,
    row3,
    51,
    'Investor Loan No.',
    textOrDash(data.investorLoanNumber),
    21,
  )
  inlineField(
    doc,
    8,
    row4,
    63,
    'Seller Loan No.',
    textOrDash(data.sellerLoanNumber),
    17,
  )
}

function drawComments(doc: Pdf, y: number, comments: string, lineStart = 0) {
  setFont(doc, 5.85)
  const lines = doc.splitTextToSize(comments || '—', WIDTH - 2) as string[]
  const availableLines = Math.max(1, Math.floor((238 - (y + 2.6)) / 2.35))
  const lineEnd = Math.min(lines.length, lineStart + availableLines)

  setFont(doc, 6.1, true)
  doc.text('Underwriter Comments', LEFT + 1.4, y)
  let cursor = y + 2.6
  setFont(doc, 5.85)
  for (let index = lineStart; index < lineEnd; index += 1) {
    doc.text(lines[index], LEFT + 1.4, cursor)
    cursor += 2.35
  }
  rule(doc, LEFT, cursor + 0.45, WIDTH)

  return { lineEnd, totalLines: lines.length, endY: cursor + 1.4 }
}

export function generateForm1008Pdf(data: Form1008Data) {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'letter',
    compress: true,
  })
  const metrics = calculateMetrics(data)

  const propertyType = enumLabel(data.propertyType, {
    '1unit': '1 unit',
    '2units': '2 units',
    '3units': '3 units',
    '4units': '4 units',
    condo: 'Condominium',
  })
  const occupancy = enumLabel(data.occupancy, {
    primary: 'Primary Residence',
    second: 'Second Home',
    investment: 'Investment Property',
  })
  const specialProperty = enumLabel(
    data.propertyKind,
    {
      pud: 'PUD',
      coop: 'Co-op',
      manufactured: 'Manufactured Housing',
    },
    'None',
  )
  const riskMethod = enumLabel(data.riskMethod, {
    manual: 'Manual Underwriting',
    aus: 'AUS',
  })
  const propertyReview = enumLabel(data.propertyReviewLevel, {
    exteriorInterior: 'Exterior/Interior',
    exteriorOnly: 'Exterior Only',
    none: 'No Appraisal',
  })

  header(doc)
  let y = 8.2

  sectionBar(doc, y, 'I. Borrower and Property Information')
  y += 5.6

  inlineField(
    doc,
    6,
    y,
    100,
    'Borrower Name',
    textOrDash(data.borrowerName),
    18,
  )
  setFont(doc, 5.75, true)
  doc.text('Occupancy Status', 119, y)
  doc.text('Sales Price', 160, y)
  setFont(doc, 6.35)
  doc.text(money(data.salesPrice), 178, y)
  rule(doc, 160, y + 1.3, 39)
  y += 2.9

  inlineField(
    doc,
    6,
    y,
    56,
    'Total # of Borrowers',
    String(data.totalBorrowers || 0),
    23,
  )
  checkbox(
    doc,
    119,
    y - 0.15,
    'Primary Residence',
    data.occupancy === 'primary',
  )
  setFont(doc, 5.75, true)
  doc.text('Appraised Value', 160, y)
  setFont(doc, 6.35)
  doc.text(money(data.appraisedValue), 178, y)
  rule(doc, 160, y + 1.3, 39)
  y += 2.9

  inlineField(
    doc,
    6,
    y,
    100,
    'Property Address',
    textOrDash(data.propertyAddress),
    19,
  )
  checkbox(doc, 119, y - 0.15, 'Second Home', data.occupancy === 'second')
  y += 2.85
  checkbox(
    doc,
    119,
    y - 0.15,
    'Investment Property',
    data.occupancy === 'investment',
  )

  const clusterY = 24.9
  checkboxList(doc, 18, clusterY, 'Property Type', [
    { label: '1 unit', selected: data.propertyType === '1unit' },
    { label: '2 units', selected: data.propertyType === '2units' },
    { label: '3 units', selected: data.propertyType === '3units' },
    { label: '4 units', selected: data.propertyType === '4units' },
    { label: 'Condominium', selected: data.propertyType === 'condo' },
    { label: 'PUD', selected: data.propertyKind === 'pud' },
    { label: 'Co-op', selected: data.propertyKind === 'coop' },
    {
      label: 'Manufactured Housing',
      selected: data.propertyKind === 'manufactured',
    },
    { label: 'Single Wide', selected: data.manufacturedWidth === 'single' },
    { label: 'Multiwide', selected: data.manufacturedWidth === 'multi' },
  ])

  setFont(doc, 6, true)
  doc.text('Project Classification', 53, clusterY)
  setFont(doc, 5.75)
  doc.text('Freddie Mac', 53, clusterY + 2.8)
  ;[
    ['Streamlined Review', false],
    ['Established Project', data.projectClassification === 'E'],
    ['New Project', data.projectClassification === 'F'],
    ['Detached Project', false],
    ['2- to 4-unit Project', false],
    ['Exempt from Review', false],
    ['Reciprocal Review', false],
  ].forEach(([label, selected], index) => {
    checkbox(
      doc,
      53,
      clusterY + 5.4 + index * 2.8,
      String(label),
      Boolean(selected),
    )
  })

  checkboxList(
    doc,
    84,
    clusterY + 2.8,
    'Fannie Mae',
    [
      {
        label: 'Established PUD Project',
        data: data.projectClassification === 'E',
      },
      { label: 'New PUD Project', data: data.projectClassification === 'F' },
      { label: 'Limited Review - New Condo Project', data: false },
      {
        label: 'Limited Review - Established Condo Project',
        data: data.projectClassification === 'Q',
      },
      { label: 'Full Review - New Condo Project', data: false },
      {
        label: 'Full Review - Established Condo Project',
        data: data.projectClassification === 'S',
      },
      { label: 'Fannie Mae Review through PERS - Condo Project', data: false },
      { label: 'U FHA-approved Condo Project', data: false },
      {
        label: 'V Condo Project Review Waived',
        data: data.projectClassification === 'V',
      },
      { label: 'Full Review - Co-op Project', data: false },
      {
        label: '2 Fannie Mae Review through PERS - Co-op Project',
        data: false,
      },
    ].map((item) => ({ label: item.label, selected: item.data })),
    2.8,
  )

  checkboxList(doc, 160, clusterY + 7.2, 'Property Rights', [
    { label: 'Fee Simple', selected: data.propertyRights === 'feeSimple' },
    { label: 'Leasehold', selected: data.propertyRights === 'leasehold' },
  ])

  inlineField(
    doc,
    12,
    65.6,
    67,
    'Project Name',
    data.projectName || specialProperty,
    15,
  )
  inlineField(
    doc,
    92,
    65.6,
    108,
    'Fannie Mae Condo Project Manager™ Project ID# (if any)',
    textOrDash(data.condoProjectId),
    61,
  )

  y = 70.7
  sectionBar(doc, y, 'II. Mortgage Information')
  y += 5.7

  checkboxList(doc, 6, y, 'Loan Type', [
    { label: 'Conventional', selected: data.loanType === 'conventional' },
    { label: 'FHA', selected: data.loanType === 'fha' },
    { label: 'VA', selected: data.loanType === 'va' },
    { label: 'USDA/RD', selected: data.loanType === 'usda' },
  ])

  checkboxList(doc, 40, y, 'Amortization Type', [
    {
      label: 'Fixed-Rate—Monthly Payments',
      selected: data.amortizationType === 'fixedMonthly',
    },
    {
      label: 'Fixed-Rate—Biweekly Payments',
      selected: data.amortizationType === 'fixedBiweekly',
    },
    { label: 'Balloon', selected: data.amortizationType === 'balloon' },
    { label: 'ARM (type)', selected: data.amortizationType === 'arm' },
    { label: 'Other (specify)', selected: data.amortizationType === 'other' },
  ])

  checkboxList(
    doc,
    83,
    y,
    'Loan Purpose',
    [
      { label: 'Purchase', selected: data.loanPurpose === 'purchase' },
      { label: 'Cash-Out Refinance', selected: data.loanPurpose === 'cashOut' },
      { label: 'Limited Cash-Out Refinance (Fannie)', selected: false },
      {
        label: 'No Cash-Out Refinance (Freddie)',
        selected: data.loanPurpose === 'rateTerm',
      },
      { label: 'Home Improvement', selected: false },
      {
        label: 'Construction Conversion/Construction to Permanent',
        selected: false,
      },
    ],
    2.75,
  )

  checkboxList(
    doc,
    142,
    y,
    'Lien Position',
    [
      { label: 'First Mortgage', selected: data.lienPosition === 'first' },
      { label: 'Amount of Subordinate Financing', selected: false },
      { label: money(data.subordinateAmount), selected: false },
      {
        label: '(If HELOC, include balance and credit limit)',
        selected: false,
      },
      { label: 'Second Mortgage', selected: data.lienPosition === 'second' },
    ],
    2.75,
  )

  inlineField(doc, 6, 100.2, 39, 'Note Information', '', 0)
  inlineField(doc, 6, 104.2, 39, 'Loan Amount', money(data.loanAmount), 15)
  inlineField(doc, 6, 108.1, 39, 'Note Rate', `${data.noteRate || 0} %`, 12)
  inlineField(
    doc,
    6,
    112,
    39,
    'Loan Term (in months)',
    String(data.loanTermMonths || 0),
    24,
  )

  setFont(doc, 6, true)
  doc.text('Mortgage Originator', 60, 100.2)
  checkbox(doc, 60, 103.6, 'Seller', data.mortgageOriginator === 'seller')
  checkbox(doc, 60, 106.4, 'Broker', data.mortgageOriginator === 'broker')
  checkbox(
    doc,
    60,
    109.2,
    'Correspondent',
    data.mortgageOriginator === 'correspondent',
  )
  inlineField(
    doc,
    60,
    113.2,
    92,
    'Broker/Correspondent Name and Company Name',
    textOrDash(data.brokerName),
    53,
  )

  setFont(doc, 6, true)
  doc.text('Temporary Buydown', 127, 100.2)
  checkbox(doc, 127, 103.6, 'Yes', data.tempBuydown)
  checkbox(doc, 127, 106.4, 'No', !data.tempBuydown)
  inlineField(
    doc,
    127,
    110.4,
    27,
    'Terms',
    data.tempBuydown ? textOrDash(data.buydownTerms) : '—',
    11,
  )

  y = 122.4
  sectionBar(doc, y, 'III. Underwriting Information')
  y += 5.7

  field(doc, 6, y, 50, "Underwriter's Name", textOrDash(data.underwriterName))
  field(
    doc,
    64,
    y,
    57,
    "Appraiser's Name/License #",
    [data.appraiserName, data.appraiserLicense].filter(Boolean).join(' · ') ||
      '—',
  )
  field(
    doc,
    130,
    y,
    67,
    'Appraisal Company Name',
    textOrDash(data.appraisalCompany),
  )

  setFont(doc, 6.1, true)
  doc.text('Stable Monthly Income', 8, 135)
  doc.text('Loan-to-Value Ratios', 69, 145)
  doc.text('Proposed Monthly Payment for the Property', 117, 135)

  const incomeRows = [
    ['Borrower 1', money(data.income1)],
    ['Borrower 2', money(data.income2)],
    ['Borrower 3', money(data.income3)],
    ['Borrower 4', money(data.income4)],
    ['Other Borrowers (5+)', money(data.incomeOther)],
    ['Rental Income - subject property', money(data.rentalIncomeSubject)],
    ['Net Rental Income - other properties', money(data.netRentalOther)],
  ]
  incomeRows.forEach(([label, value], index) => {
    amountLine(doc, 8, 139 + index * 3.05, label, value, 58, 18)
  })
  amountLine(
    doc,
    8,
    160.4,
    'Total Borrower Income',
    money(metrics.totalIncome),
    58,
    18,
  )
  checkbox(
    doc,
    31,
    163.2,
    'At least one borrower is self-employed',
    data.selfEmployed,
  )

  amountLine(doc, 69, 148.5, 'LTV', percent(metrics.ltv), 26, 15)
  amountLine(doc, 69, 151.7, 'CLTV/TLTV', percent(metrics.cltv), 26, 15)
  amountLine(doc, 69, 154.9, 'HCLTV/HTLTV', percent(metrics.hcltv), 26, 15)

  const paymentRows = [
    ['First Mortgage P&I', money(metrics.firstMortgagePI)],
    ['Subordinate Lien(s) P&I', money(data.subordinatePI)],
    ["Homeowner's Insurance", money(data.homeownersInsurance)],
    ['Supplemental Property Insurance', money(data.supplementalInsurance)],
    ['Property Taxes', money(data.propertyTaxes)],
    ['Mortgage Insurance', money(data.mortgageInsurance)],
    ['Association/Project Dues (Condo, Co-Op, PUD)', money(data.hoaDues)],
    ['Other', money(data.otherHousing)],
  ]
  paymentRows.forEach(([label, value], index) => {
    amountLine(doc, 117, 139 + index * 3.05, label, value, 82, 24)
  })
  amountLine(doc, 117, 163.4, 'Total', money(metrics.totalHousing), 82, 24)

  setFont(doc, 6.1, true)
  doc.text('Qualifying Ratios', 8, 169)
  doc.text('Level of Property Review', 69, 169)
  doc.text('All Other Monthly Payments Used in Qualifying', 117, 169)
  checkbox(doc, 8, 172.2, 'DSCR (Investor)', data.qualMethod === 'dscr')
  checkbox(doc, 8, 175, 'Full-Doc DTI', data.qualMethod !== 'dscr')
  amountLine(
    doc,
    8,
    179.4,
    'Primary Housing Expense/Income',
    data.qualMethod === 'dscr' ? 'N/A %' : percent(metrics.housingRatio),
    58,
    16,
  )
  amountLine(
    doc,
    8,
    182.8,
    'Total Obligations/Income(DTI)',
    data.qualMethod === 'dscr' ? 'N/A %' : percent(metrics.dti),
    58,
    16,
  )
  checkbox(
    doc,
    69,
    172.2,
    'Exterior/Interior',
    data.propertyReviewLevel === 'exteriorInterior',
  )
  checkbox(
    doc,
    69,
    175,
    'Exterior Only',
    data.propertyReviewLevel === 'exteriorOnly',
  )
  checkbox(doc, 69, 177.8, 'No Appraisal', data.propertyReviewLevel === 'none')
  inlineField(
    doc,
    69,
    185.2,
    42,
    'Form Number',
    textOrDash(data.appraisalFormNumber),
    17,
  )
  amountLine(doc, 117, 175.2, '', money(data.allOtherPayments), 82, 24)

  setFont(doc, 6.1, true)
  doc.text('Qualifying Rate', 8, 189)
  checkbox(
    doc,
    8,
    192.2,
    'Rate Used for Qualifying',
    data.qualRateType === 'rateUsed',
  )
  checkbox(
    doc,
    8,
    195,
    'Initial Bought-Down Rate',
    data.qualRateType === 'initialBoughtDown',
  )
  checkbox(doc, 8, 197.8, 'Other', data.qualRateType === 'other')
  amountLine(doc, 8, 192.2, '', `${data.qualRate || 0} %`, 58, 15)

  setFont(doc, 6.1, true)
  doc.text('Escrow (T&I)', 82, 189)
  checkbox(doc, 82, 192.2, 'Yes', data.escrowTI)
  checkbox(doc, 94, 192.2, 'No', !data.escrowTI)

  setFont(doc, 6.1, true)
  doc.text('Borrower Funds to Close', 137, 185.2)
  amountLine(doc, 145, 189.1, 'Required', money(data.fundsRequired), 53, 18)
  amountLine(
    doc,
    145,
    192.7,
    'Verified Assets',
    money(data.verifiedAssets),
    53,
    18,
  )
  inlineField(
    doc,
    117,
    197.8,
    35,
    'No. of Months Reserves',
    String(data.monthsReserves || 0),
    27,
  )

  setFont(doc, 6.1, true)
  doc.text('Risk Assessment', 8, 204)
  checkbox(doc, 8, 207.2, 'Manual Underwriting', data.riskMethod === 'manual')
  checkbox(doc, 8, 210, 'AUS', data.riskMethod === 'aus')
  checkbox(doc, 8, 212.8, 'DU', data.ausType === 'du')
  checkbox(doc, 19, 212.8, 'LPA', data.ausType === 'lpa')
  checkbox(doc, 31, 212.8, 'Other', data.ausType === 'other')

  setFont(doc, 6.1, true)
  doc.text('Affordable Housing Initiative', 69, 204)
  checkbox(doc, 69, 207.2, 'Yes', data.affordableHousing)
  checkbox(doc, 81, 207.2, 'No', !data.affordableHousing)

  setFont(doc, 6.1, true)
  doc.text('Homeownership Education Certificate in File', 137, 204)
  checkbox(doc, 137, 207.2, 'Yes', data.homeownershipEd)
  checkbox(doc, 149, 207.2, 'No', !data.homeownershipEd)

  inlineField(
    doc,
    8,
    217.2,
    63,
    'AUS Recommendation',
    data.riskMethod === 'manual'
      ? data.ausRecommendation || 'N/A'
      : textOrDash(data.ausRecommendation),
    22,
  )
  inlineField(
    doc,
    69,
    217.2,
    27,
    'Rep. Score',
    String(data.repCreditScore || 0),
    15,
  )
  inlineField(
    doc,
    104,
    217.2,
    37,
    'Interested Party Contributions',
    percent(data.interestedPartyContrib || 0, 1),
    28,
  )
  inlineField(
    doc,
    149,
    217.2,
    49,
    'AUS Type',
    data.riskMethod === 'manual' ? 'N/A' : riskMethod,
    12,
  )

  const commentResult = drawComments(doc, 222.4, data.underwriterComments)

  if (commentResult.lineEnd < commentResult.totalLines) {
    doc.addPage()
    header(doc, true)
    sectionBar(doc, 8.2, 'III. Underwriting Information — Continuation')
    const continued = drawComments(
      doc,
      14,
      data.underwriterComments,
      commentResult.lineEnd,
    )
    drawSectionFour(doc, Math.max(continued.endY + 5, 220), data)
  } else {
    drawSectionFour(doc, 242.5, data)
  }

  footer(doc)
  doc.save(
    `Form-1008-${safeFileName(data.loanNumber) || 'underwriting-summary'}.pdf`,
  )
}
